/**
 * Curated destinations for the navigation assistant.
 * Route paths stay authoritative in routes.js; this catalog adds ranking and i18n keys.
 */
export const navigationAssistantCatalog = [
  {
    id: 'about',
    path: '/about',
    titleKey: 'nav.about',
    descriptionKey: 'assistant.routes.about.description',
    keywordsKey: 'assistant.routes.about.keywords',
    keywords: {
      en: ['about', 'project', 'partners', 'mission', 'information'],
      de: ['uber uns', 'projekt', 'partner', 'mission', 'informationen'],
      fr: ['a propos', 'projet', 'partenaires', 'mission', 'informations'],
      nl: ['over ons', 'project', 'partners', 'missie', 'informatie'],
    },
    priority: 80,
  },
  {
    id: 'contact',
    path: '/contact',
    titleKey: 'nav.contact',
    descriptionKey: 'assistant.routes.contact.description',
    keywordsKey: 'assistant.routes.contact.keywords',
    keywords: {
      en: ['contact', 'email', 'support', 'help', 'team'],
      de: ['kontakt', 'email', 'hilfe', 'support', 'team'],
      fr: ['contact', 'email', 'aide', 'support', 'equipe'],
      nl: ['contact', 'email', 'hulp', 'support', 'team'],
    },
    priority: 82,
  },
  {
    id: 'deliverables',
    path: '/deliverables',
    titleKey: 'nav.deliverables',
    descriptionKey: 'assistant.routes.deliverables.description',
    keywordsKey: 'assistant.routes.deliverables.keywords',
    keywords: {
      en: ['deliverables', 'results', 'outputs', 'milestones', 'documents'],
      de: ['projektergebnisse', 'ergebnisse', 'meilensteine', 'dokumente'],
      fr: ['livrables', 'resultats', 'jalons', 'documents'],
      nl: ['projectresultaten', 'resultaten', 'mijlpalen', 'documenten'],
    },
    priority: 70,
  },
  {
    id: 'terms',
    path: '/terms',
    titleKey: 'nav.terms',
    descriptionKey: 'assistant.routes.terms.description',
    keywordsKey: 'assistant.routes.terms.keywords',
    keywords: {
      en: ['terms', 'conditions', 'legal', 'privacy', 'policy'],
      de: ['agb', 'bedingungen', 'rechtlich', 'datenschutz', 'richtlinie'],
      fr: ['conditions', 'legal', 'confidentialite', 'politique'],
      nl: ['voorwaarden', 'juridisch', 'privacy', 'beleid'],
    },
    priority: 64,
  },
  {
    id: 'simulations',
    path: '/simulations',
    titleKey: 'nav.simulations',
    descriptionKey: 'assistant.routes.simulations.description',
    keywordsKey: 'assistant.routes.simulations.keywords',
    keywords: {
      en: ['simulations', 'simulation', 'simulator', 'tools', 'practice'],
      de: ['simulationen', 'simulation', 'simulator', 'werkzeuge', 'ubung'],
      fr: ['simulations', 'simulation', 'simulateur', 'outils', 'pratique'],
      nl: ['simulaties', 'simulatie', 'simulator', 'tools', 'oefenen'],
    },
    priority: 90,
  },
  {
    id: 'heatPumpSimulation',
    path: '/simulations/heat-pump-simulation',
    titleKey: 'assistant.routes.heatPumpSimulation.title',
    descriptionKey: 'assistant.routes.heatPumpSimulation.description',
    keywordsKey: 'assistant.routes.heatPumpSimulation.keywords',
    titles: {
      en: 'Heat Pump Simulation',
      de: 'Warmepumpen-Simulation',
      fr: 'Simulation de pompe a chaleur',
      nl: 'Warmtepompsimulatie',
    },
    descriptions: {
      en: 'Explore heat pump sizing, temperatures, emitters, and system behavior.',
      de: 'Erkunden Sie Auslegung, Temperaturen, Heizkorper und Systemverhalten von Warmepumpen.',
      fr: 'Explorez le dimensionnement, les temperatures, les emetteurs et le comportement du systeme.',
      nl: 'Verken dimensionering, temperaturen, radiatoren en systeemgedrag van warmtepompen.',
    },
    keywords: {
      en: ['heat pump', 'simulator', 'heating', 'radiator', 'sizing'],
      de: ['warmepumpe', 'simulator', 'heizung', 'heizkorper', 'auslegung'],
      fr: ['pompe a chaleur', 'simulateur', 'chauffage', 'radiateur', 'dimensionnement'],
      nl: ['warmtepomp', 'simulator', 'verwarming', 'radiator', 'dimensionering'],
    },
    priority: 92,
  },
  {
    id: 'hydraulicBalancing',
    path: '/simulations/hydraulic-balancing',
    titleKey: 'assistant.routes.hydraulicBalancing.title',
    descriptionKey: 'assistant.routes.hydraulicBalancing.description',
    keywordsKey: 'assistant.routes.hydraulicBalancing.keywords',
    titles: {
      en: 'Hydraulic Balancing',
      de: 'Hydraulischer Abgleich',
      fr: 'Equilibrage hydraulique',
      nl: 'Hydraulisch inregelen',
    },
    descriptions: {
      en: 'Practice valve settings, flow rates, and pressure balance across rooms.',
      de: 'Uben Sie Ventileinstellungen, Durchflusse und Druckabgleich uber mehrere Raume.',
      fr: 'Travaillez les reglages de vannes, debits et equilibres de pression entre les pieces.',
      nl: 'Oefen met klepinstellingen, debieten en drukbalans over meerdere ruimtes.',
    },
    keywords: {
      en: ['hydraulic balancing', 'flow', 'valves', 'pressure', 'rooms'],
      de: ['hydraulischer abgleich', 'durchfluss', 'ventile', 'druck', 'raume'],
      fr: ['equilibrage hydraulique', 'debit', 'vannes', 'pression', 'pieces'],
      nl: ['hydraulisch inregelen', 'debiet', 'kleppen', 'druk', 'ruimtes'],
    },
    priority: 88,
  },
  {
    id: 'hybridSystemIntegration',
    path: '/simulations/hybrid-system-integration',
    titleKey: 'assistant.routes.hybridSystemIntegration.title',
    descriptionKey: 'assistant.routes.hybridSystemIntegration.description',
    keywordsKey: 'assistant.routes.hybridSystemIntegration.keywords',
    titles: {
      en: 'Hybrid System Integration Tool',
      de: 'Werkzeug fur hybride Systemintegration',
      fr: 'Outil d integration de systeme hybride',
      nl: 'Tool voor hybride systeemintegratie',
    },
    descriptions: {
      en: 'Compare source dispatch, cost, and carbon decisions in hybrid systems.',
      de: 'Vergleichen Sie Quelleneinsatz, Kosten und CO2-Entscheidungen in hybriden Systemen.',
      fr: 'Comparez la repartition des sources, les couts et les emissions carbone.',
      nl: 'Vergelijk broninzet, kosten en CO2-keuzes in hybride systemen.',
    },
    keywords: {
      en: ['hybrid', 'integration', 'carbon', 'cost', 'dispatch'],
      de: ['hybrid', 'integration', 'co2', 'kosten', 'einsatz'],
      fr: ['hybride', 'integration', 'carbone', 'cout', 'repartition'],
      nl: ['hybride', 'integratie', 'co2', 'kosten', 'inzet'],
    },
    priority: 76,
  },
  {
    id: 'trainingCurriculum',
    path: '/training-curriculum',
    titleKey: 'nav.trainingCurriculum',
    descriptionKey: 'assistant.routes.trainingCurriculum.description',
    keywordsKey: 'assistant.routes.trainingCurriculum.keywords',
    keywords: {
      en: ['curriculum', 'course', 'training', 'modules', 'lessons'],
      de: ['curriculum', 'kurs', 'schulung', 'module', 'lektionen'],
      fr: ['programme', 'cours', 'formation', 'modules', 'lecons'],
      nl: ['curriculum', 'cursus', 'training', 'modules', 'lessen'],
    },
    priority: 84,
  },
  {
    id: 'events',
    path: '/events',
    titleKey: 'nav.events',
    descriptionKey: 'assistant.routes.events.description',
    keywordsKey: 'assistant.routes.events.keywords',
    keywords: {
      en: ['events', 'event', 'register', 'workshop', 'session'],
      de: ['veranstaltungen', 'event', 'anmelden', 'workshop', 'sitzung'],
      fr: ['evenements', 'evenement', 'inscription', 'atelier', 'session'],
      nl: ['evenementen', 'evenement', 'registreren', 'workshop', 'sessie'],
    },
    priority: 86,
  },
  {
    id: 'navigationAssistant',
    path: '/navigation-assistant',
    titleKey: 'nav.navigationAssistant',
    descriptionKey: 'assistant.routes.navigationAssistant.description',
    keywordsKey: 'assistant.routes.navigationAssistant.keywords',
    descriptions: {
      en: 'Open the guided assistant that helps users find pages and content.',
      de: 'Offnen Sie den gefuhrten Assistenten, der Nutzer zu Seiten und Inhalten leitet.',
      fr: 'Ouvrez l assistant guide qui aide les utilisateurs a trouver des pages et des contenus.',
      nl: 'Open de begeleide assistent die gebruikers helpt pagina s en inhoud te vinden.',
    },
    keywords: {
      en: ['assistant', 'chatbot', 'navigation assistant', 'help me find pages', 'compass'],
      de: ['assistent', 'chatbot', 'navigationsassistent', 'seiten finden', 'kompass'],
      fr: ['assistant', 'chatbot', 'assistant de navigation', 'trouver des pages', 'boussole'],
      nl: ['assistent', 'chatbot', 'navigatieassistent', 'pagina s vinden', 'kompas'],
    },
    priority: 83,
  },
  {
    id: 'news',
    path: '/news',
    titleKey: 'nav.news',
    descriptionKey: 'assistant.routes.news.description',
    keywordsKey: 'assistant.routes.news.keywords',
    keywords: {
      en: ['news', 'updates', 'articles', 'announcements'],
      de: ['nachrichten', 'updates', 'artikel', 'ankundigungen'],
      fr: ['actualites', 'mises a jour', 'articles', 'annonces'],
      nl: ['nieuws', 'updates', 'artikelen', 'aankondigingen'],
    },
    priority: 78,
  },
  {
    id: 'profile',
    path: '/profile',
    titleKey: 'nav.profile',
    descriptionKey: 'assistant.routes.profile.description',
    keywordsKey: 'assistant.routes.profile.keywords',
    intents: ['account-profile'],
    ambiguityGroup: 'profiles',
    keywords: {
      en: ['profile', 'account', 'settings', 'preferences', 'language'],
      de: ['profil', 'konto', 'einstellungen', 'sprache', 'praferenzen'],
      fr: ['profil', 'compte', 'parametres', 'preferences', 'langue'],
      nl: ['profiel', 'account', 'instellingen', 'voorkeuren', 'taal'],
    },
    priority: 74,
  },
  {
    id: 'surveys',
    path: '/surveys',
    titleKey: 'nav.surveys',
    descriptionKey: 'assistant.routes.surveys.description',
    keywordsKey: 'assistant.routes.surveys.keywords',
    keywords: {
      en: ['surveys', 'survey', 'questionnaire', 'forms', 'responses'],
      de: ['umfragen', 'umfrage', 'fragebogen', 'formulare', 'antworten'],
      fr: ['sondages', 'sondage', 'questionnaire', 'formulaires', 'reponses'],
      nl: ['enquetes', 'enquete', 'vragenlijst', 'formulieren', 'reacties'],
    },
    priority: 76,
  },
  {
    id: 'quizCollections',
    path: '/quiz-collections',
    titleKey: 'nav.quizCollections',
    descriptionKey: 'assistant.routes.quizCollections.description',
    keywordsKey: 'assistant.routes.quizCollections.keywords',
    keywords: {
      en: ['quiz', 'quizzes', 'questions', 'assessment', 'test'],
      de: ['quiz', 'fragen', 'bewertung', 'test', 'quizsammlung'],
      fr: ['quiz', 'questions', 'evaluation', 'test', 'collection'],
      nl: ['quiz', 'vragen', 'toets', 'test', 'verzameling'],
    },
    priority: 78,
  },
  {
    id: 'learningPaths',
    path: '/learning-paths',
    titleKey: 'nav.learningPaths',
    descriptionKey: 'assistant.routes.learningPaths.description',
    keywordsKey: 'assistant.routes.learningPaths.keywords',
    keywords: {
      en: ['learning path', 'learning paths', 'roadmap', 'journey'],
      de: ['lernpfad', 'lernpfade', 'lernreise', 'fahrplan'],
      fr: ['parcours', 'parcours d apprentissage', 'feuille de route'],
      nl: ['leertraject', 'leertrajecten', 'leerroute', 'roadmap'],
    },
    priority: 80,
  },
  {
    id: 'manageEvents',
    path: '/admin/events',
    titleKey: 'nav.manageEvents',
    descriptionKey: 'assistant.routes.manageEvents.description',
    keywordsKey: 'assistant.routes.manageEvents.keywords',
    keywords: {
      en: ['manage events', 'event editor', 'event management', 'schedule'],
      de: ['veranstaltungen verwalten', 'event editor', 'eventverwaltung', 'planung'],
      fr: ['gerer les evenements', 'editeur evenement', 'gestion evenements'],
      nl: ['evenementen beheren', 'evenement editor', 'eventbeheer'],
    },
    priority: 72,
  },
  {
    id: 'manageNews',
    path: '/admin/news',
    titleKey: 'nav.manageNews',
    descriptionKey: 'assistant.routes.manageNews.description',
    keywordsKey: 'assistant.routes.manageNews.keywords',
    keywords: {
      en: ['manage news', 'news editor', 'articles', 'news management'],
      de: ['nachrichten verwalten', 'news editor', 'artikel', 'newsverwaltung'],
      fr: ['gerer les actualites', 'editeur actualites', 'articles'],
      nl: ['nieuws beheren', 'nieuwseditor', 'artikelen'],
    },
    priority: 72,
  },
  {
    id: 'trainingProfiles',
    path: '/training-profiles',
    titleKey: 'nav.trainingProfiles',
    descriptionKey: 'assistant.routes.trainingProfiles.description',
    keywordsKey: 'assistant.routes.trainingProfiles.keywords',
    intents: ['training-profile'],
    ambiguityGroup: 'profiles',
    keywords: {
      en: ['training profiles', 'simulation profiles', 'trainer profiles', 'trainer'],
      de: ['trainingsprofile', 'simulationsprofile', 'trainerprofile', 'trainer'],
      fr: ['profils de formation', 'profils de simulation', 'profils formateur', 'formateur'],
      nl: ['trainingsprofielen', 'simulatieprofielen', 'trainerprofielen', 'trainer'],
    },
    priority: 74,
  },
  {
    id: 'users',
    path: '/admin/users',
    titleKey: 'nav.users',
    descriptionKey: 'assistant.routes.users.description',
    keywordsKey: 'assistant.routes.users.keywords',
    keywords: {
      en: ['users', 'accounts', 'approval', 'roles', 'admin'],
      de: ['benutzer', 'konten', 'freigabe', 'rollen', 'admin'],
      fr: ['utilisateurs', 'comptes', 'approbation', 'roles', 'admin'],
      nl: ['gebruikers', 'accounts', 'goedkeuring', 'rollen', 'admin'],
    },
    priority: 70,
  },
  {
    id: 'countries',
    path: '/admin/countries',
    titleKey: 'nav.countries',
    descriptionKey: 'assistant.routes.countries.description',
    keywordsKey: 'assistant.routes.countries.keywords',
    keywords: {
      en: ['countries', 'country', 'locales', 'language metadata'],
      de: ['lander', 'land', 'sprachen', 'metadaten'],
      fr: ['pays', 'langues', 'metadonnees'],
      nl: ['landen', 'land', 'talen', 'metadata'],
    },
    priority: 62,
  },
  {
    id: 'trainingCurriculumAssignments',
    path: '/admin/training-curriculum',
    titleKey: 'nav.trainingCurriculumAssignments',
    descriptionKey: 'assistant.routes.trainingCurriculumAssignments.description',
    keywordsKey: 'assistant.routes.trainingCurriculumAssignments.keywords',
    keywords: {
      en: ['curriculum assignments', 'curriculum mapping', 'country curriculum'],
      de: ['curriculum zuordnungen', 'curriculum mapping', 'land curriculum'],
      fr: ['attributions programme', 'mappage programme', 'programme pays'],
      nl: ['curriculumtoewijzingen', 'curriculummapping', 'land curriculum'],
    },
    priority: 58,
  },
  {
    id: 'levelsOfDifficulty',
    path: '/admin/levels-of-difficulty',
    titleKey: 'nav.levelsOfDifficulty',
    descriptionKey: 'assistant.routes.levelsOfDifficulty.description',
    keywordsKey: 'assistant.routes.levelsOfDifficulty.keywords',
    keywords: {
      en: ['difficulty', 'levels', 'categories', 'level metadata'],
      de: ['schwierigkeit', 'stufen', 'kategorien', 'metadaten'],
      fr: ['difficulte', 'niveaux', 'categories', 'metadonnees'],
      nl: ['moeilijkheid', 'niveaus', 'categorieen', 'metadata'],
    },
    priority: 56,
  },
  {
    id: 'exceptionLogs',
    path: '/admin/exception-logs',
    titleKey: 'nav.exceptionLogs',
    descriptionKey: 'assistant.routes.exceptionLogs.description',
    keywordsKey: 'assistant.routes.exceptionLogs.keywords',
    keywords: {
      en: ['exception logs', 'errors', 'diagnostics', 'logs'],
      de: ['fehlerprotokolle', 'fehler', 'diagnose', 'logs'],
      fr: ['journaux erreurs', 'erreurs', 'diagnostic', 'logs'],
      nl: ['foutlogboeken', 'fouten', 'diagnostiek', 'logs'],
    },
    priority: 56,
  },
  {
    id: 'fileStorage',
    path: '/admin/file-storage',
    titleKey: 'nav.fileStorage',
    descriptionKey: 'assistant.routes.fileStorage.description',
    keywordsKey: 'assistant.routes.fileStorage.keywords',
    keywords: {
      en: ['file storage', 'files', 'uploads', 'media', 'storage health'],
      de: ['dateispeicher', 'dateien', 'uploads', 'medien', 'speicher'],
      fr: ['stockage fichiers', 'fichiers', 'televersements', 'medias'],
      nl: ['bestandsopslag', 'bestanden', 'uploads', 'media'],
    },
    priority: 60,
  },
  {
    id: 'deletedFiles',
    path: '/admin/deleted-files',
    titleKey: 'nav.deletedFiles',
    descriptionKey: 'assistant.routes.deletedFiles.description',
    keywordsKey: 'assistant.routes.deletedFiles.keywords',
    keywords: {
      en: ['deleted files', 'deletion', 'audit', 'removed files'],
      de: ['geloschte dateien', 'loschung', 'audit', 'entfernte dateien'],
      fr: ['fichiers supprimes', 'suppression', 'audit'],
      nl: ['verwijderde bestanden', 'verwijdering', 'audit'],
    },
    priority: 54,
  },
  {
    id: 'dataDeletionLogs',
    path: '/admin/data-deletion-logs',
    titleKey: 'nav.dataDeletionLogs',
    descriptionKey: 'assistant.routes.dataDeletionLogs.description',
    keywordsKey: 'assistant.routes.dataDeletionLogs.keywords',
    keywords: {
      en: ['data deletion', 'deletion logs', 'privacy', 'audit trail'],
      de: ['datenloschung', 'loschprotokolle', 'datenschutz', 'audit'],
      fr: ['suppression donnees', 'journaux suppression', 'confidentialite', 'audit'],
      nl: ['gegevensverwijdering', 'verwijderingslogboeken', 'privacy', 'audit'],
    },
    priority: 54,
  },
];

export const navigationAssistantFallbackPaths = [
  '/simulations',
  '/training-curriculum',
  '/events',
  '/news',
  '/about',
  '/contact',
  '/profile',
];